import { z } from 'zod';
import { ECONOMY_BALANCE } from './economy.js';

export const ShipClassSchema = z.enum(['scout', 'construction', 'corvette', 'frigate', 'cruiser']);
export type ShipClass = z.infer<typeof ShipClassSchema>;

export const MilitaryShipClassSchema = z.enum(['corvette', 'frigate', 'cruiser']);
export type MilitaryShipClass = z.infer<typeof MilitaryShipClassSchema>;

export const FleetStatusSchema = z.enum(['idle', 'traveling', 'surveying', 'constructing', 'in_combat', 'destroyed', 'disbanded']);
export type FleetStatus = z.infer<typeof FleetStatusSchema>;

export const OrderStatusSchema = z.enum(['pending', 'in_progress', 'completed', 'failed']);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const ShipClientSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  class: ShipClassSchema,
  maxHull: z.number().int().optional(),
  currentHull: z.number().int().optional(),
  maxShield: z.number().int().optional(),
  currentShield: z.number().int().optional(),
  armor: z.number().int().optional(),
});
export type ShipClient = z.infer<typeof ShipClientSchema>;

export const FleetOrderClientSchema = z.object({
  id: z.string().uuid(),
  orderType: z.enum(['travel', 'survey']),
  fromSystemId: z.string().uuid().nullable(),
  toSystemId: z.string().uuid().nullable(),
  issuedAt: z.string().datetime(),
  arrivesAt: z.string().datetime().nullable(),
  completesAt: z.string().datetime().nullable(),
  status: OrderStatusSchema,
});
export type FleetOrderClient = z.infer<typeof FleetOrderClientSchema>;

export const FleetRoleSchema = z.enum(['standard', 'reserve']);
export type FleetRole = z.infer<typeof FleetRoleSchema>;

export const FleetCapabilitiesSchema = z.object({
  military: z.boolean(),
  survey: z.boolean(),
  construction: z.boolean(),
});
export type FleetCapabilities = z.infer<typeof FleetCapabilitiesSchema>;

/** Informational aggregates; militaryStrength is an estimate, not a combat prediction. */
export const FleetStatsSchema = z.object({
  shipCounts: z.record(z.string(), z.number().int()),
  totalShips: z.number().int(),
  militaryShips: z.number().int(),
  damagedShips: z.number().int(),
  combatReadyShips: z.number().int(),
  militaryStrength: z.number().int(),
  /** Fleet speed in ly/h: the slowest ship plus the tech bonus. */
  speedLyPerHour: z.number(),
  /** Server GAME_SPEED (1 in production); a jump takes distance / speed hours / gameSpeed. */
  gameSpeed: z.number().positive().default(1),
  /** Ships lost in battles this fleet fought. */
  losses: z.number().int(),
  /** Metal maintenance per hour of this fleet's warships (ECONOMY_BALANCE.metalUpkeepPerHour); informational. */
  upkeepMetalPerHour: z.number().int().default(0),
});
export type FleetStats = z.infer<typeof FleetStatsSchema>;


// ---- Fleet command queue (M6/8) ----

export const FleetCommandTypeSchema = z.enum(['move', 'survey', 'hold', 'patrol', 'auto_survey']);
export type FleetCommandType = z.infer<typeof FleetCommandTypeSchema>;

export const FleetCommandStatusSchema = z.enum(['queued', 'active', 'completed', 'cancelled', 'failed']);
export type FleetCommandStatus = z.infer<typeof FleetCommandStatusSchema>;

export const FleetCommandClientSchema = z.object({
  id: z.string().uuid(),
  type: FleetCommandTypeSchema,
  status: FleetCommandStatusSchema,
  position: z.number().int(),
  targetSystemId: z.string().uuid().nullable(),
  route: z.array(z.string().uuid()).nullable(),
  routeIndex: z.number().int().nullable(),
  laps: z.number().int().nullable(),
  durationSeconds: z.number().int().nullable(),
  holdUntil: z.string().datetime().nullable(),
  surveyedCount: z.number().int().nullable(),
  resultReason: z.string().nullable(),
  createdAt: z.string().datetime(),
  startedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
});
export type FleetCommandClient = z.infer<typeof FleetCommandClientSchema>;

export const FleetCommandSummarySchema = z.object({
  active: FleetCommandClientSchema.nullable(),
  queued: z.number().int(),
  automation: z.enum(['auto_survey', 'patrol']).nullable(),
});
export type FleetCommandSummary = z.infer<typeof FleetCommandSummarySchema>;

export const FleetClientSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: FleetRoleSchema,
  currentSystemId: z.string().uuid(),
  destinationSystemId: z.string().uuid().nullable(),
  eta: z.string().datetime().nullable(),
  status: FleetStatusSchema,
  version: z.number().int(),
  ships: z.array(ShipClientSchema),
  activeOrder: FleetOrderClientSchema.nullable(),
  capabilities: FleetCapabilitiesSchema,
  stats: FleetStatsSchema,
  commands: FleetCommandSummarySchema,
  /** Player-defined fleet group (see FleetGroupClientSchema), or null. */
  groupId: z.string().uuid().nullable().default(null),
});
export type FleetClient = z.infer<typeof FleetClientSchema>;

// API request/response

export const TravelCommandSchema = z.object({
  toSystemId: z.string().uuid(),
  idempotencyKey: z.string().min(1).max(128),
});
export type TravelCommand = z.infer<typeof TravelCommandSchema>;

export const FleetListResponseSchema = z.object({
  fleets: z.array(FleetClientSchema),
});
export type FleetListResponse = z.infer<typeof FleetListResponseSchema>;

// ---- Fleet management (M6/7) ----

export const FleetNameSchema = z.string().trim().min(1, 'A név nem lehet üres').max(40, 'Legfeljebb 40 karakter');
const ShipIdListSchema = z.array(z.string().uuid()).min(1, 'Válassz legalább egy hajót').max(200);
const IdempotencyKeySchema = z.string().min(1).max(128);

/** New fleet from ships that are in idle fleets of the same system. */
export const CreateFleetCommandSchema = z.object({
  shipIds: ShipIdListSchema,
  name: FleetNameSchema.optional(),
  idempotencyKey: IdempotencyKeySchema,
});
export type CreateFleetCommand = z.infer<typeof CreateFleetCommandSchema>;

/** Moves the selected ships of this fleet into a new fleet (at least one ship must stay). */
export const SplitFleetCommandSchema = CreateFleetCommandSchema;
export type SplitFleetCommand = CreateFleetCommand;

/** Moves every ship of sourceFleetId into this fleet; the source is disbanded. */
export const MergeFleetCommandSchema = z.object({
  sourceFleetId: z.string().uuid(),
  idempotencyKey: IdempotencyKeySchema,
});
export type MergeFleetCommand = z.infer<typeof MergeFleetCommandSchema>;

/** Assigns ships from other idle fleets in the same system to this fleet. */
export const TransferShipsCommandSchema = z.object({
  shipIds: ShipIdListSchema,
  idempotencyKey: IdempotencyKeySchema,
});
export type TransferShipsCommand = z.infer<typeof TransferShipsCommandSchema>;

export const RenameFleetCommandSchema = z.object({ name: FleetNameSchema });
export type RenameFleetCommand = z.infer<typeof RenameFleetCommandSchema>;

export const SurveyCommandSchema = z.object({ idempotencyKey: IdempotencyKeySchema });
export type SurveyCommand = z.infer<typeof SurveyCommandSchema>;

export const FleetMutationResponseSchema = z.object({ fleets: z.array(FleetClientSchema) });
export type FleetMutationResponse = z.infer<typeof FleetMutationResponseSchema>;

const CmdKey = z.string().min(1).max(128);
const SystemId = z.string().uuid();

/** Appends a command to the fleet's queue. MOVE/SURVEY target any known system (multi-hop over known lanes). */
export const EnqueueFleetCommandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('move'), targetSystemId: SystemId, idempotencyKey: CmdKey }),
  z.object({ type: z.literal('survey'), targetSystemId: SystemId, idempotencyKey: CmdKey }),
  z.object({
    type: z.literal('hold'),
    durationSeconds: z.number().int().min(1, 'Legalább 1 másodperc').max(86_400, 'Legfeljebb 24 óra'),
    idempotencyKey: CmdKey,
  }),
  z.object({
    type: z.literal('patrol'),
    route: z.array(SystemId).min(2, 'A járőrútvonalhoz legalább 2 rendszer kell').max(6, 'Legfeljebb 6 rendszer választható'),
    idempotencyKey: CmdKey,
  }),
  z.object({ type: z.literal('auto_survey'), idempotencyKey: CmdKey }),
]);
export type EnqueueFleetCommand = z.infer<typeof EnqueueFleetCommandSchema>;

/** The client's view of the queued commands in the desired order; rejected if the queue changed meanwhile. */
export const ReorderFleetCommandsSchema = z.object({
  commandIds: z.array(z.string().uuid()).max(20),
});
export type ReorderFleetCommands = z.infer<typeof ReorderFleetCommandsSchema>;

export const FleetCommandsResponseSchema = z.object({
  commands: z.array(FleetCommandClientSchema),
  /** Planned route through systems known to the player (current position first). */
  plannedPath: z.array(z.string().uuid()),
});
export type FleetCommandsResponse = z.infer<typeof FleetCommandsResponseSchema>;

// ---- M10.11: Javítás (idő + fém) ----

export const RepairCommandSchema = z.object({ idempotencyKey: IdempotencyKeySchema });
export type RepairCommand = z.infer<typeof RepairCommandSchema>;

export const RepairClientSchema = z.object({
  id: z.string().uuid(),
  fleetId: z.string().uuid(),
  systemId: z.string().uuid(),
  metalCost: z.number().int(),
  shipCount: z.number().int(),
  startedAt: z.string().datetime(),
  completesAt: z.string().datetime(),
});
export type RepairClient = z.infer<typeof RepairClientSchema>;

/** A flotta javítási ajánlata: mi hiányzik, mibe kerül, folyamatban van-e már javítás. */
export const RepairInfoSchema = z.object({
  quote: z.object({ shipCount: z.number().int(), metal: z.number().int(), timeMs: z.number().int() }),
  /** Lehet-e most elindítani (a szabályok szerint); `reason` ha nem. */
  canRepair: z.boolean(),
  reason: z.string().nullable(),
  open: RepairClientSchema.nullable(),
});
export type RepairInfo = z.infer<typeof RepairInfoSchema>;

/** Metal maintenance per hour of a composition; one formula for server stats and client previews. */
export function fleetMetalUpkeepPerHour(shipCounts: Record<string, number>): number {
  const table = ECONOMY_BALANCE.metalUpkeepPerHour as Record<string, number>;
  let total = 0;
  for (const [cls, n] of Object.entries(shipCounts)) total += (table[cls] ?? 0) * n;
  return total;
}

// ---- Fleet groups (named, colored sets of own fleets) ----

export const FLEET_GROUP_LIMIT = 12;
export const FLEET_GROUP_MEMBER_LIMIT = 50;
export const BULK_FLEET_LIMIT = 25;

export const FleetGroupNameSchema = z.string().trim().min(1, 'A név nem lehet üres').max(24, 'Legfeljebb 24 karakter');
export const FleetGroupColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Érvénytelen szín');

export const FleetGroupClientSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string(),
  fleetIds: z.array(z.string().uuid()),
});
export type FleetGroupClient = z.infer<typeof FleetGroupClientSchema>;

export const FleetGroupListResponseSchema = z.object({ groups: z.array(FleetGroupClientSchema) });
export type FleetGroupListResponse = z.infer<typeof FleetGroupListResponseSchema>;

export const CreateFleetGroupSchema = z.object({
  name: FleetGroupNameSchema,
  color: FleetGroupColorSchema.optional(),
  idempotencyKey: IdempotencyKeySchema,
});
export type CreateFleetGroupCommand = z.infer<typeof CreateFleetGroupSchema>;

export const UpdateFleetGroupSchema = z.object({
  name: FleetGroupNameSchema.optional(),
  color: FleetGroupColorSchema.optional(),
}).refine(v => v.name !== undefined || v.color !== undefined, { message: 'Nincs mit módosítani' });
export type UpdateFleetGroupCommand = z.infer<typeof UpdateFleetGroupSchema>;

/** Sets the group membership: `fleetIds` are added; with `remove` they are taken out of the group instead. */
export const FleetGroupMembersSchema = z.object({
  fleetIds: z.array(z.string().uuid()).min(1, 'Válassz legalább egy flottát').max(FLEET_GROUP_MEMBER_LIMIT),
  remove: z.boolean().optional(),
});
export type FleetGroupMembersCommand = z.infer<typeof FleetGroupMembersSchema>;

// ---- Route preview (server-computed; the client never decides distance, time or reachability) ----

export const RoutePreviewRequestSchema = z.object({ targetSystemId: z.string().uuid() });
export type RoutePreviewRequest = z.infer<typeof RoutePreviewRequestSchema>;

export const RouteHopSchema = z.object({
  systemId: z.string().uuid(),
  distanceLy: z.number(),
  durationMs: z.number().int(),
});
export type RouteHop = z.infer<typeof RouteHopSchema>;

export const RoutePreviewSchema = z.object({
  fromSystemId: z.string().uuid(),
  hops: z.array(RouteHopSchema),
  totalDistanceLy: z.number(),
  totalDurationMs: z.number().int(),
  /** Earliest arrival if the fleet leaves now (idle fleet) — for a travelling fleet it is counted from its arrival. */
  arrivesAt: z.string().datetime(),
  speedLyPerHour: z.number(),
  gameSpeed: z.number().positive(),
  /** Metal maintenance per hour the fleet costs while on its way. */
  upkeepMetalPerHour: z.number().int(),
});
export type RoutePreview = z.infer<typeof RoutePreviewSchema>;

// ---- Bulk commands ----

export const BulkFleetCommandSchema = z.object({
  fleetIds: z.array(z.string().uuid()).min(1, 'Válassz legalább egy flottát').max(BULK_FLEET_LIMIT, `Legfeljebb ${BULK_FLEET_LIMIT} flotta egyszerre`),
  command: z.discriminatedUnion('type', [
    z.object({ type: z.literal('move'), targetSystemId: z.string().uuid() }),
    z.object({ type: z.literal('hold'), durationSeconds: z.number().int().min(1).max(86_400) }),
    z.object({ type: z.literal('stop') }),
  ]),
  /** Each fleet derives its own key from it, so a retried batch never double-issues. Max 80 chars. */
  idempotencyKey: z.string().min(1).max(80),
  /** Clears each fleet's command queue first (once per batch key), so the command starts right away. */
  replace: z.boolean().optional(),
});
export type BulkFleetCommand = z.infer<typeof BulkFleetCommandSchema>;

export const BulkFleetResultSchema = z.object({
  fleetId: z.string().uuid(),
  ok: z.boolean(),
  error: z.string().nullable(),
  message: z.string().nullable(),
});
export type BulkFleetResult = z.infer<typeof BulkFleetResultSchema>;

export const BulkFleetResponseSchema = z.object({ results: z.array(BulkFleetResultSchema) });
export type BulkFleetResponse = z.infer<typeof BulkFleetResponseSchema>;
